/**
 * @signetix/presentation-schema — TypeScript model of `signetix.presentation` v1 and v2
 * (v2 = Signetix Logic: rules, element logic, variants, asset bindings; docs/SIGNETIX_LOGIC.md).
 *
 * Normative description: docs/PRESENTATION_SCHEMA.md. JSON Schemas:
 * schema/presentation.v1.schema.json, schema/presentation.v2.schema.json. When these disagree, the document wins and both
 * artefacts must be fixed in the same change.
 *
 * Reader rule: consumers MUST ignore unknown properties and unknown element types
 * (forward compatibility within major version 1). Types below therefore describe what a
 * v1 reader understands, not an exhaustive closed set.
 */
export const PRESENTATION_SCHEMA_ID = 'signetix.presentation';
export const PRESENTATION_SCHEMA_VERSION = 1;
export const PRESENTATION_SCHEMA_URI = 'https://schemas.signetix.io/presentation/v1.json';
/** Presentation document v2 (Signetix Logic, docs/SIGNETIX_LOGIC.md §3). */
export const PRESENTATION_SCHEMA_V2_URI = 'https://schemas.signetix.io/presentation/v2.json';
/** Major versions this package describes. */
export const PRESENTATION_SCHEMA_VERSIONS = [1, 2];
/** Properties a variant may override, per element type (`*` = every type). docs/SIGNETIX_LOGIC.md §3.2. */
export const VARIANT_PROPS = {
    '*': ['opacity'],
    text: ['content', 'color', 'background_color', 'font', 'align'],
    shape: ['fill', 'stroke'],
    icon: ['color', 'asset'],
    image: ['asset', 'fit'],
    video: ['asset'],
    qr: ['value', 'foreground', 'background'],
    clock: ['color', 'format'],
    date: ['color', 'format'],
    ticker: ['items', 'color'],
};
/** Whether `prop` may be overridden by a variant of an element of `type`. */
export function isVariantProp(type, prop) {
    return (VARIANT_PROPS['*'] ?? []).indexOf(prop) >= 0 || (VARIANT_PROPS[type] ?? []).indexOf(prop) >= 0;
}
/** Template filters introduced by v2 (docs/SIGNETIX_LOGIC.md §3.3). */
export const V2_FILTERS = ['prefix', 'suffix', 'round', 'unit'];
/** `unit:"<name>"` symbols; output is value + U+00A0 + symbol. */
export const UNIT_SYMBOLS = {
    celsius: '°C', fahrenheit: '°F', percent: '%', kmh: 'km/h', mph: 'mph', ms: 'm/s',
    mm: 'mm', cm: 'cm', m: 'm', km: 'km', g: 'g', kg: 'kg', lb: 'lb', l: 'l', ml: 'ml', kwh: 'kWh', w: 'W',
};
export const KNOWN_ELEMENT_TYPES = [
    'text',
    'image',
    'video',
    'shape',
    'icon',
    'clock',
    'date',
    'weather',
    'qr',
    'rss',
    'ticker',
    'web',
    'html',
];
export function isKnownElement(el) {
    return KNOWN_ELEMENT_TYPES.includes(el.type);
}
/** Families every v1 renderer bundles; any other family must be embedded via `fonts`. */
export const BUNDLED_FONT_FAMILIES = ['Inter', 'Noto Sans', 'Noto Serif', 'Roboto Mono'];
/**
 * RFC 8785 (JCS) canonical JSON serialisation.
 *
 * Valid for JSON values produced by JSON.parse (finite numbers only). Object keys are
 * sorted by UTF-16 code units (default JS sort), numbers/strings use ECMAScript
 * serialisation exactly as JCS requires.
 */
export function canonicalize(value) {
    if (value === null || typeof value === 'boolean' || typeof value === 'string') {
        return JSON.stringify(value);
    }
    if (typeof value === 'number') {
        if (!Number.isFinite(value))
            throw new TypeError('JCS: non-finite number');
        return JSON.stringify(value);
    }
    if (Array.isArray(value)) {
        return '[' + value.map((v) => canonicalize(v)).join(',') + ']';
    }
    if (typeof value === 'object') {
        const obj = value;
        const keys = Object.keys(obj)
            .filter((k) => obj[k] !== undefined)
            .sort();
        return '{' + keys.map((k) => JSON.stringify(k) + ':' + canonicalize(obj[k])).join(',') + '}';
    }
    throw new TypeError(`JCS: unsupported type ${typeof value}`);
}
/** The exact string whose SHA-256 is the document checksum: JCS of the document minus `checksum` and `$schema`. */
export function checksumInput(doc) {
    const { checksum: _checksum, $schema: _schema, ...rest } = doc;
    return canonicalize(rest);
}
/** Extracts `{{ alias.path | filter }}` expressions from a template. Escaped `\{{` is literal. */
export function parseTemplateExpressions(template) {
    const out = [];
    const re = /(^|[^\\])\{\{\s*([^}]*?)\s*\}\}/g;
    let m;
    while ((m = re.exec(template)) !== null) {
        out.push(m[2] ?? '');
    }
    return out;
}
/** Binding path of an expression (`weather.current.temperature | number:0` → `weather.current.temperature`). */
export function expressionPath(expression) {
    return (expression.split('|')[0] ?? '').trim();
}
//# sourceMappingURL=index.js.map